/** Sessão de app — demo (VITE_AUTH_MODE=demo) ou derivada do JWT (api). */

export type DemoRole = "EMPLOYEE" | "MANAGER" | "HR";

export type CompanyOption = { id: string; name: string };

export type AppSession = {
  userId: string;
  displayName: string;
  email: string;
  role: DemoRole | string;
  permissions: readonly string[];
  companies: readonly CompanyOption[];
  companyId: string;
};

/** @deprecated use AppSession */
export type DemoSession = AppSession;

/**
 * Espelha `packages/database/src/rbac/catalog.ts` (SYSTEM_ROLES).
 * Gestor = papel MANAGER do catálogo + autoatendimento que o catálogo omite
 * (holerite, docs, benefícios, ponto) — sem employee:write (evita menu RH).
 */
const EMPLOYEE_PERMISSIONS = [
  "employee:read",
  "employee:read_sensitive",
  "employee:write",
  "payroll:read",
  "vacation:read",
  "vacation:request",
  "request:read",
  "request:write",
  "document:read",
  "benefit:read",
  "timecard:read",
  "org:read",
  "lgpd:export_own",
] as const;

const MANAGER_PERMISSIONS = [
  "employee:read",
  "vacation:read",
  "vacation:request",
  "vacation:approve",
  "request:read",
  "request:write",
  "request:resolve",
  "org:read",
  "analytics:read",
  "user:read",
  "lgpd:export_own",
  // Autoatendimento (colaborador que também é gestor)
  "payroll:read",
  "document:read",
  "benefit:read",
  "timecard:read",
  "employee:read_sensitive",
] as const;

const HR_PERMISSIONS = [
  "employee:read",
  "employee:read_sensitive",
  "employee:write",
  "employee:export",
  "employee:salary_history",
  "payroll:read",
  "vacation:read",
  "vacation:request",
  "vacation:approve",
  "vacation:approve_on_behalf",
  "request:read",
  "request:write",
  "request:resolve",
  "document:read",
  "document:write",
  "benefit:read",
  "timecard:read",
  "org:read",
  "org:write",
  "analytics:read",
  "analytics:read_cost",
  "integration:read",
  "sync:trigger",
  "user:read",
  "user:invite",
  "user:manage",
  "audit:read",
  "settings:manage",
  "lgpd:export_own",
] as const;

const ROLE_PERMISSIONS: Record<DemoRole, readonly string[]> = {
  EMPLOYEE: EMPLOYEE_PERMISSIONS,
  MANAGER: MANAGER_PERMISSIONS,
  HR: HR_PERMISSIONS,
};

const COMPANIES: CompanyOption[] = [
  { id: "co-matriz", name: "Acme Matriz" },
  { id: "co-filial", name: "Acme Filial SP" },
];

export function buildDemoSession(role: DemoRole): AppSession {
  return {
    userId: `demo-${role.toLowerCase()}`,
    displayName:
      role === "EMPLOYEE"
        ? "Ana Colaboradora"
        : role === "MANAGER"
          ? "Bruno Gestor"
          : "Carla RH",
    email: `${role.toLowerCase()}@demo.local`,
    role,
    permissions: ROLE_PERMISSIONS[role],
    companies: COMPANIES,
    companyId: COMPANIES[0]!.id,
  };
}

export function hasPermission(
  session: Pick<AppSession, "permissions">,
  required: readonly string[] | undefined,
): boolean {
  if (!required || required.length === 0) return true;
  if (session.permissions.includes("*")) return true;
  return required.every((p) => session.permissions.includes(p));
}
